import React from "react";
import { Document, Image, Link, Page, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import {
  commonStyles,
  formatDate,
  formatPhoneNumber,
  isValidSummary,
  parseHtmlToText,
  renderBulletPoints,
} from "../Services/ReactPDFService.jsx";

// Modern Slate Template - Clean design with dark slate header and profile photo
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    backgroundColor: "#ffffff",
    paddingTop: 30,
    paddingBottom: 30,
    paddingHorizontal: 40,
  },
  // Header with dark slate background
  headerContainer: {
    backgroundColor: "#ffffff",
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
  headerContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flex: 1,
    paddingRight: 20,
  },
  // Name and title
  name: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1e293b",
    marginBottom: 0,
    letterSpacing: 0.5,
    lineHeight: 1.2,
  },
  title: {
    fontSize: 11,
    color: "#64748b",
    fontStyle: "italic",
    marginBottom: 14,
    marginTop: -0,
    lineHeight: 1.2,
  },
  // Contact info with icons in two columns
  contactGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  contactColumn: {
    width: "48%",
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },
  contactIconBox: {
    width: 18,
    height: 18,
    backgroundColor: "#f1f5f9",
    borderRadius: 3,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 8,
  },
  contactIcon: {
    width: 10,
    height: 10,
  },
  contactText: {
    fontSize: 9,
    color: "#475569",
    flex: 1,
  },
  // Profile photo
  photoContainer: {
    width: 90,
    height: 90,
    borderRadius: 45,
    overflow: "hidden",
    backgroundColor: "#f1f5f9",
  },
  profilePhoto: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  // Main content
  mainContent: {
    paddingHorizontal: 0,
    paddingTop: 10,
  },
  // Section styling
  sectionContainer: {
    marginBottom: 18,
  },
  sectionTitleContainer: {
    backgroundColor: "#6a7991",
    paddingVertical: 5,
    paddingHorizontal: 12,
    marginBottom: 10,
    alignItems: "center",
    width: "100%",
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: "#ffffff",
    textTransform: "uppercase",
    letterSpacing: 2,
    textAlign: "center",
    lineHeight: 1.3,
  },
  // Profile/Summary section
  summaryText: {
    fontSize: 10,
    lineHeight: 1.6,
    color: "#475569",
    textAlign: "justify",
  },
  // Experience items - two column layout
  experienceItem: {
    flexDirection: "row",
    marginBottom: 14,
  },
  experienceLeft: {
    width: "20%",
    paddingRight: 0,
  },
  experienceRight: {
    width: "80%",
  },
  companyTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#1e293b",
  },
  jobPosition: {
    fontSize: 10,
    color: "#64748b",
    fontStyle: "italic",
    marginTop: 2,
  },
  dateLocation: {
    fontSize: 11,
    color: "#64748b",
    marginTop: 2,
  },
  bulletPoint: {
    fontSize: 10,
    marginBottom: 3,
    paddingLeft: 12,
    lineHeight: 1.5,
    color: "#475569",
  },
  // Education items
  educationItem: {
    marginBottom: 12,
  },
  educationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 2,
  },
  degree: {
    fontSize: 11,
    fontWeight: 700,
    color: "#1e293b",
  },
  school: {
    fontSize: 10,
    color: "#64748b",
    fontStyle: "italic",
    marginTop: 1,
  },
  educationDescription: {
    fontSize: 10,
    lineHeight: 1.5,
    color: "#475569",
    marginTop: 4,
  },
  // Skills section - 3 column grid
  skillsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  skillItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    width: "33.33%",
    marginBottom: 4,
    paddingRight: 8,
  },
  skillBullet: {
    fontSize: 10,
    color: "#1e293b",
    marginRight: 4,
  },
  skillText: {
    fontSize: 10,
    color: "#1e293b",
    flex: 1,
  },
  // Languages section
  languageItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },
  languageName: {
    fontSize: 10,
    color: "#1e293b",
    width: 80,
  },
  languageLevel: {
    fontSize: 9,
    color: "#64748b",
  },
  // Certifications
  certificationItem: {
    marginBottom: 8,
  },
  certName: {
    fontSize: 10,
    fontWeight: 700,
    color: "#1e293b",
  },
  certAuthority: {
    fontSize: 9,
    color: "#64748b",
  },
  // Projects
  projectItem: {
    marginBottom: 10,
  },
  projectTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#1e293b",
  },
  projectTech: {
    fontSize: 9,
    color: "#64748b",
    fontStyle: "italic",
    marginTop: 1,
  },
});

const ModernSlateTemplate = ({ resumeData, customStyles = {}, customSectionOrder = null }) => {
  const {
    personalInfo = {},
    workExperience = [],
    education = [],
    skills = {},
    projects = [],
    certifications = [],
    achievements = [],
    languages = [],
    awards = [],
    volunteering = [],
    interests = [],
    industryExpertise = [],
    publications = [],
    references = [],
    customSections = [],
    sectionTitles = {},
    summary = "",
    professionalTitle = "",
    profilePhoto = {},
  } = resumeData || {};

  // Get profile photo URL
  const profilePhotoUrl = profilePhoto?.url || personalInfo?.profilePhotoUrl || null;

  // Create dynamic styles based on customizations
  const createDynamicStyles = (baseStyles, customizations) => {
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;
    if (!hasCustomizations) return baseStyles;

    const { fontSize, sectionSpacing, titleColor, subtitleColor, fontFamily } = customizations;
    const customStyleOverrides = {};

    if (fontFamily) {
      customStyleOverrides.page = { ...baseStyles.page, fontFamily };
    }

    if (fontSize) {
      customStyleOverrides.name = { ...baseStyles.name, fontSize: fontSize * 2 };
      customStyleOverrides.title = { ...baseStyles.title, fontSize: fontSize * 0.9 };
      customStyleOverrides.sectionTitle = { ...baseStyles.sectionTitle, fontSize: fontSize * 0.85 };
      customStyleOverrides.companyTitle = { ...baseStyles.companyTitle, fontSize: fontSize * 0.8 };
      customStyleOverrides.degree = { ...baseStyles.degree, fontSize: fontSize * 0.8 };
      customStyleOverrides.summaryText = { ...baseStyles.summaryText, fontSize: fontSize * 0.72 };
      customStyleOverrides.bulletPoint = { ...baseStyles.bulletPoint, fontSize: fontSize * 0.72 };
    }

    if (sectionSpacing) {
      customStyleOverrides.sectionContainer = { ...baseStyles.sectionContainer, marginBottom: sectionSpacing };
    }

    if (titleColor) {
      customStyleOverrides.name = { ...(customStyleOverrides.name || baseStyles.name), color: titleColor };
      customStyleOverrides.sectionTitleContainer = {
        ...(customStyleOverrides.sectionTitleContainer || baseStyles.sectionTitleContainer),
        backgroundColor: titleColor,
      };
    }

    return StyleSheet.create({ ...baseStyles, ...customStyleOverrides });
  };

  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Default section titles
  const defaultTitles = {
    summary: 'Profile', workExperience: 'Experience', education: 'Education', skills: 'Skills',
    projects: 'Projects', certifications: 'Certifications', achievements: 'Achievements',
    languages: 'Languages', awards: 'Awards', volunteering: 'Volunteering', interests: 'Interests',
    industryExpertise: 'Industry Expertise', publications: 'Publications', references: 'References',
  };
  const getSectionTitle = (key) => sectionTitles[key] || defaultTitles[key];

  // Default section order
  const defaultSectionOrder = ["summary", "workExperience", "education", "skills", "projects", "certifications", "achievements", "languages", "awards", "volunteering", "interests", "industryExpertise", "publications", "references"];

  // Build final section order - include custom sections
  const customSectionIds = customSections.filter(cs => cs.id && cs.title && cs.items?.length > 0).map(cs => cs.id);
  const baseSectionOrder = customSectionOrder || defaultSectionOrder;
  const finalSectionOrder = [...baseSectionOrder, ...customSectionIds.filter(id => !baseSectionOrder.includes(id))];

  // Combine all skills for display
  const allSkills = (() => {
    if (Array.isArray(skills)) return skills;
    if (skills.list && Array.isArray(skills.list) && skills.list.length > 0) return skills.list;
    const combinedSkills = [
      ...(skills.technical || []),
      ...(skills.soft || []),
      ...(skills.tools || []),
      ...(skills.other || []),
    ];
    if (combinedSkills.length > 0) return combinedSkills;
    if (skills.categorized) {
      return [
        ...(skills.categorized.technical || []),
        ...(skills.categorized.soft || []),
        ...(skills.categorized.tools || []),
        ...(skills.categorized.other || []),
      ];
    }
    return [];
  })();

  const getSkillName = (skill) => {
    if (typeof skill === "string") return skill;
    if (skill && typeof skill === "object") return skill.name || skill.skill || "";
    return "";
  };

  // Helper to get language name from string or object
  const getLanguageName = (lang) => {
    if (typeof lang === "string") return lang;
    if (lang && typeof lang === "object") return lang.name || lang.language || "";
    return "";
  };

  // Helper to get interest name from string or object
  const getInterestName = (interest) => {
    if (typeof interest === "string") return interest;
    if (interest && typeof interest === "object") return interest.name || interest.title || "";
    return "";
  };

  // Section components
  const sectionComponents = {
    summary: () =>
      summary &&
      isValidSummary(summary) && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            <Text style={mergedStyles.sectionTitle}>Profile</Text>
          </View>
          <Text style={mergedStyles.summaryText}>{parseHtmlToText(summary)}</Text>
        </View>
      ),

    workExperience: () =>
      workExperience.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            <Text style={mergedStyles.sectionTitle}>Experience</Text>
          </View>
          {workExperience.map((job, index) => (
            <View key={index} style={mergedStyles.experienceItem}>
              <View style={mergedStyles.experienceLeft}>
                <Text style={mergedStyles.companyTitle}>{job.company}</Text>
                {job.jobTitle && <Text style={mergedStyles.jobPosition}>{job.jobTitle}</Text>}
                <Text style={mergedStyles.dateLocation}>
                  {formatDate(job.startDate)} –{" "}
                  {job.isCurrentJob || job.endDate === "Present" || job.endDate === "Current" || !job.endDate
                    ? formatDate(job.endDate) || "Present"
                    : formatDate(job.endDate)}
                </Text>
                {job.location && <Text style={mergedStyles.dateLocation}>{job.location}</Text>}
              </View>
              <View style={mergedStyles.experienceRight}>
                {job.description && renderBulletPoints(parseHtmlToText(job.description), mergedStyles)}
              </View>
            </View>
          ))}
        </View>
      ),

    education: () =>
      education.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            <Text style={mergedStyles.sectionTitle}>Education</Text>
          </View>
          {education.map((edu, index) => (
            <View key={index} style={mergedStyles.educationItem}>
              <View style={mergedStyles.educationHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={mergedStyles.degree}>
                    {edu.degree}
                    {edu.fieldOfStudy ? `, ${edu.fieldOfStudy}` : ""}
                  </Text>
                  <Text style={mergedStyles.school}>{edu.school}</Text>
                </View>
                <View style={{ alignItems: "flex-end" }}>
                  <Text style={mergedStyles.dateLocation}>
                    {formatDate(edu.startDate)} – {edu.endDate === "Present" ? "Present" : formatDate(edu.endDate)}
                  </Text>
                  {edu.location && <Text style={mergedStyles.dateLocation}>{edu.location}</Text>}
                </View>
              </View>
              {edu.description && (
                <Text style={mergedStyles.educationDescription}>{parseHtmlToText(edu.description)}</Text>
              )}
            </View>
          ))}
        </View>
      ),

    skills: () =>
      allSkills.length > 0 && (
        <View style={mergedStyles.sectionContainer} wrap={false}>
          <View style={mergedStyles.sectionTitleContainer}>
            <Text style={mergedStyles.sectionTitle}>Skills</Text>
          </View>
          <View style={mergedStyles.skillsContainer}>
            {allSkills.slice(0, 15).map((skill, index) => (
              <View key={index} style={mergedStyles.skillItem}>
                <Text style={mergedStyles.skillBullet}>•</Text>
                <Text style={mergedStyles.skillText}>{getSkillName(skill)}</Text>
              </View>
            ))}
          </View>
        </View>
      ),

    projects: () =>
      projects.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            <Text style={mergedStyles.sectionTitle}>Projects</Text>
          </View>
          {projects.map((project, index) => (
            <View key={index} style={mergedStyles.projectItem}>
              <Text style={mergedStyles.projectTitle}>{project.name}</Text>
              {project.technologies && <Text style={mergedStyles.projectTech}>Technologies: {project.technologies}</Text>}
              {project.description && (
                <View style={{ marginTop: 4 }}>{renderBulletPoints(parseHtmlToText(project.description), mergedStyles)}</View>
              )}
            </View>
          ))}
        </View>
      ),

    certifications: () =>
      certifications.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            <Text style={mergedStyles.sectionTitle}>Certifications</Text>
          </View>
          {certifications.map((cert, index) => (
            <View key={index} style={mergedStyles.certificationItem}>
              <Text style={mergedStyles.certName}>{cert.name}</Text>
              <Text style={mergedStyles.certAuthority}>
                {cert.authority || cert.issuer}
                {cert.date && ` • ${formatDate(cert.date)}`}
              </Text>
            </View>
          ))}
        </View>
      ),

    languages: () =>
      languages &&
      languages.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            <Text style={mergedStyles.sectionTitle}>Languages</Text>
          </View>
          {languages.map((lang, index) => (
            <View key={index} style={mergedStyles.languageItem}>
              <Text style={mergedStyles.languageName}>{getLanguageName(lang)}</Text>
              {lang.proficiency && <Text style={mergedStyles.languageLevel}>– {lang.proficiency}</Text>}
            </View>
          ))}
        </View>
      ),

    achievements: () =>
      achievements.length > 0 && (
        <View style={mergedStyles.sectionContainer}>
          <View style={mergedStyles.sectionTitleContainer}>
            <Text style={mergedStyles.sectionTitle}>{getSectionTitle('achievements')}</Text>
          </View>
          {achievements.map((achievement, index) => (
            <View key={index} style={{ marginBottom: 6 }}>
              {achievement.description && (
                <Text style={mergedStyles.summaryText}>{parseHtmlToText(achievement.description)}</Text>
              )}
            </View>
          ))}
        </View>
      ),

    awards: () => awards.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('awards')}</Text>
        </View>
        {awards.map((award, index) => (
          <View key={index} style={mergedStyles.certificationItem}>
            <Text style={mergedStyles.certName}>{award.title || award.name}</Text>
            <Text style={mergedStyles.certAuthority}>
              {award.issuer}{award.date && ` • ${formatDate(award.date)}`}
            </Text>
          </View>
        ))}
      </View>
    ),

    volunteering: () => volunteering.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('volunteering')}</Text>
        </View>
        {volunteering.map((vol, index) => (
          <View key={index} style={mergedStyles.experienceItem}>
            <View style={mergedStyles.experienceHeader}>
              <Text style={mergedStyles.companyTitle}>{vol.role}</Text>
              {(vol.startDate || vol.endDate) && (
                <Text style={mergedStyles.dateRange}>
                  {formatDate(vol.startDate)} – {vol.isCurrentRole ? "Present" : formatDate(vol.endDate)}
                </Text>
              )}
            </View>
            <Text style={mergedStyles.companyName}>{vol.organization}</Text>
            {vol.description && <View style={{ marginTop: 4 }}>{renderBulletPoints(parseHtmlToText(vol.description), mergedStyles)}</View>}
          </View>
        ))}
      </View>
    ),

    interests: () => interests.length > 0 && (
      <View style={mergedStyles.sectionContainer} wrap={false}>
        <View style={mergedStyles.sectionTitleContainer}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('interests')}</Text>
        </View>
        <View style={mergedStyles.skillsContainer}>
          {interests.map((interest, index) => (
            <View key={index} style={mergedStyles.skillPill}>
              <Text style={mergedStyles.skillText}>{getInterestName(interest)}</Text>
            </View>
          ))}
        </View>
      </View>
    ),

    industryExpertise: () => industryExpertise.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('industryExpertise')}</Text>
        </View>
        {industryExpertise.map((exp, index) => (
          <View key={index} style={mergedStyles.certificationItem}>
            <Text style={mergedStyles.certName}>{exp.name}</Text>
            <Text style={mergedStyles.certAuthority}>
              {exp.proficiency}{exp.yearsOfExperience && ` • ${exp.yearsOfExperience} years`}
            </Text>
          </View>
        ))}
      </View>
    ),

    publications: () => publications.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('publications')}</Text>
        </View>
        {publications.map((pub, index) => (
          <View key={index} style={mergedStyles.certificationItem}>
            <Text style={mergedStyles.certName}>{pub.title}</Text>
            <Text style={mergedStyles.certAuthority}>
              {pub.publisher}{pub.date && ` • ${formatDate(pub.date)}`}
            </Text>
          </View>
        ))}
      </View>
    ),

    references: () => references.length > 0 && (
      <View style={mergedStyles.sectionContainer}>
        <View style={mergedStyles.sectionTitleContainer}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('references')}</Text>
        </View>
        {references.map((ref, index) => (
          <View key={index} style={mergedStyles.certificationItem}>
            <Text style={mergedStyles.certName}>{ref.name}</Text>
            <Text style={mergedStyles.certAuthority}>
              {ref.position}{ref.company && ` at ${ref.company}`}{ref.email && ` • ${ref.email}`}
            </Text>
          </View>
        ))}
      </View>
    ),

    // Dynamic custom sections
    ...customSections.reduce((acc, customSection) => {
      if (customSection.id && customSection.title && customSection.items?.length > 0) {
        acc[customSection.id] = () => (
          <View style={mergedStyles.sectionContainer}>
            <View style={mergedStyles.sectionTitleContainer}>
              <Text style={mergedStyles.sectionTitle}>{customSection.title}</Text>
            </View>
            {customSection.items.map((item, itemIndex) => (
              <View key={itemIndex} style={{ marginBottom: 6 }}>
                {item.content && <Text style={mergedStyles.summaryText}>{parseHtmlToText(item.content)}</Text>}
              </View>
            ))}
          </View>
        );
      }
      return acc;
    }, {}),
  };

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page} wrap={true}>
        {/* Header Section */}
        <View style={mergedStyles.headerContainer}>
          <View style={mergedStyles.headerContent}>
            <View style={mergedStyles.headerLeft}>
              {/* Name */}
              <Text style={mergedStyles.name}>
                {personalInfo.firstName} {personalInfo.lastName}
              </Text>

              {/* Professional Title */}
              {(professionalTitle || (workExperience.length > 0 && workExperience[0].jobTitle)) && (
                <Text style={mergedStyles.title}>{professionalTitle || workExperience[0].jobTitle}</Text>
              )}

              {/* Contact Info Grid */}
              <View style={mergedStyles.contactGrid}>
                <View style={mergedStyles.contactColumn}>
                  {/* Email */}
                  {personalInfo.email && (
                    <View style={mergedStyles.contactItem}>
                      <View style={mergedStyles.contactIconBox}>
                        <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
                          <Path
                            d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                            fill="#64748b"
                          />
                        </Svg>
                      </View>
                      <Text style={mergedStyles.contactText}>{personalInfo.email}</Text>
                    </View>
                  )}

                  {/* Location */}
                  {personalInfo.location && (
                    <View style={mergedStyles.contactItem}>
                      <View style={mergedStyles.contactIconBox}>
                        <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
                          <Path
                            d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                            fill="#64748b"
                          />
                        </Svg>
                      </View>
                      <Text style={mergedStyles.contactText}>{personalInfo.location}</Text>
                    </View>
                  )}

                  {/* Nationality */}
                  {personalInfo.nationality && (
                    <View style={mergedStyles.contactItem}>
                      <View style={mergedStyles.contactIconBox}>
                        <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
                          <Path
                            d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"
                            fill="#64748b"
                          />
                        </Svg>
                      </View>
                      <Text style={mergedStyles.contactText}>{personalInfo.nationality}</Text>
                    </View>
                  )}
                </View>

                <View style={mergedStyles.contactColumn}>
                  {/* Phone */}
                  {personalInfo.phone && (
                    <View style={mergedStyles.contactItem}>
                      <View style={mergedStyles.contactIconBox}>
                        <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
                          <Path
                            d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
                            fill="#64748b"
                          />
                        </Svg>
                      </View>
                      <Text style={mergedStyles.contactText}>{formatPhoneNumber(personalInfo.phone)}</Text>
                    </View>
                  )}

                  {/* Date of Birth */}
                  {personalInfo.dateOfBirth && (
                    <View style={mergedStyles.contactItem}>
                      <View style={mergedStyles.contactIconBox}>
                        <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
                          <Path
                            d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"
                            fill="#64748b"
                          />
                        </Svg>
                      </View>
                      <Text style={mergedStyles.contactText}>{personalInfo.dateOfBirth}</Text>
                    </View>
                  )}

                  {/* LinkedIn */}
                  {personalInfo.linkedin && (
                    <View style={mergedStyles.contactItem}>
                      <View style={mergedStyles.contactIconBox}>
                        <Svg style={mergedStyles.contactIcon} viewBox="0 0 24 24">
                          <Path
                            d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"
                            fill="#64748b"
                          />
                        </Svg>
                      </View>
                      <Text style={mergedStyles.contactText}>{personalInfo.linkedin}</Text>
                    </View>
                  )}
                </View>
              </View>
            </View>

            {/* Profile Photo */}
            {profilePhotoUrl && (
              <View style={mergedStyles.photoContainer}>
                <Image src={profilePhotoUrl} style={mergedStyles.profilePhoto} />
              </View>
            )}
          </View>
        </View>

        {/* Main Content */}
        <View style={mergedStyles.mainContent}>
          {finalSectionOrder.map((sectionId, index) => {
            const SectionComponent = sectionComponents[sectionId];
            const content = SectionComponent ? SectionComponent() : null;
            return content ? <View key={`${sectionId}-${index}`} wrap={true}>{content}</View> : null;
          })}
        </View>
      </Page>
    </Document>
  );
};

export default ModernSlateTemplate;
